import { createContext, useContext, type ReactNode } from "react";
import type { DiscoverItem, DiscoverCity, DiscoverDetail, TripPreset, UtilityMode } from "./types";
import type { TripBasketLine, TripStopOffer } from "./lib/trip-booking";
import type { BucketId } from "./lib/buckets";

/**
 * How a trip's days reach the host's bucket.
 *
 * This package has no bucket and no activity API of its own, so the host
 * lends both. Left out (a city site), the days still show their estimate and
 * no booking button appears.
 */
export interface TripBookingAdapter {
  /** The activity's packages, for pricing and booking. Null when it cannot be booked. */
  fetchOffer(slug: string, lang: string): Promise<TripStopOffer | null>;
  /** Put the lines in the bucket. Resolves with how many went in. */
  addToBucket(lines: readonly TripBasketLine[]): Promise<number>;
  /** Take the guest to the bucket. */
  openBucket(): void;
  /**
   * A hotel's live price for some nights and a party: its cheapest room, in
   * the currency the guest picked (a stay is charged online in that currency,
   * so the plan shows the number the card statement will). Null when it has
   * no room for those dates. Left out, hotels show no price in the plan.
   */
  fetchStayQuote?(query: StayQuoteQuery): Promise<StayQuote | null>;
  /** A stay amount in its own currency, the way the host prints money. */
  formatStayAmount?(amount: number, currency: string): string;
}

export interface StayQuoteQuery {
  readonly slug: string;
  readonly checkin: string;
  readonly checkout: string;
  readonly adults: number;
  readonly childAges: readonly number[];
  readonly lang: string;
}

export interface StayQuote {
  readonly amount: number;
  readonly currency: string;
}

/**
 * The host's saved list, lent so a Discover heart is the same heart as every
 * other one on the site. Without it, Discover kept its own list in the
 * browser: a guest hearted a balloon on its page, opened Liked, and found it
 * empty, because Liked only ever read Discover's private list.
 */
export interface DiscoverSavedAdapter {
  isSaved(item: DiscoverItem): boolean;
  setSaved(item: DiscoverItem, saved: boolean): void;
}

export interface DiscoverDataSource {
  fetchIndex(lang?: string): Promise<{ items: DiscoverItem[]; cities: DiscoverCity[] }>;
  fetchDetail(slug: string, lang?: string): Promise<DiscoverDetail>;
  getInitialItems(): DiscoverItem[];
}

export interface DiscoverConfig {
  dataSource: DiscoverDataSource;
  meta?: { heroLabel?: string; tripPresets?: readonly TripPreset[] };
  features?: { aiChat?: ReactNode };
  /**
   * The URL a rail mode lives at, so the four sections are real links a
   * crawler can follow and a guest can bookmark rather than four buttons
   * flipping client state at one address.
   *
   * Left out, the rail builds `/{lang}/discover` from the active i18n
   * language — right for this app, overridable for a city-guide mounted
   * somewhere else.
   */
  railHref?: (mode: UtilityMode, lang: string) => string;
  /**
   * A hook returning the host's price formatter (dirhams in, the guest's
   * chosen currency out). The app passes `useDisplayPrice`, so a guest who
   * picked USD sees dollars on Discover like everywhere else. Left out, the
   * server's `priceLabel` shows as-is. Must be the same hook on every render.
   */
  usePrice?: () => (madValue: number) => string;
  /**
   * A hook returning the host's trip booking adapter (see TripBookingAdapter).
   * Must be the same hook on every render.
   */
  useTripBooking?: () => TripBookingAdapter | null;
  /**
   * A hook returning the host's saved list (see DiscoverSavedAdapter). Left
   * out (a city site), hearts stay in this browser. Must be the same hook on
   * every render.
   */
  useSaved?: () => DiscoverSavedAdapter;
  /**
   * The host's own 3D category art, one image URL per category chip on the
   * phone sheet. A category left out shows its label alone.
   */
  bucketArt?: Partial<Record<BucketId, string>>;
}

const DiscoverConfigContext = createContext<DiscoverConfig | null>(null);

export function DiscoverConfigProvider({
  config,
  children,
}: {
  config: DiscoverConfig;
  children: ReactNode;
}) {
  return (
    <DiscoverConfigContext.Provider value={config}>
      {children}
    </DiscoverConfigContext.Provider>
  );
}

function useNoPrice(): null {
  return null;
}

/**
 * `(item) => the price to show`: converted through the host's `usePrice`
 * when the item carries a dirham price, otherwise its `priceLabel`.
 */
export function useItemPrice(): (item: DiscoverItem) => string | null {
  const ctx = useContext(DiscoverConfigContext);
  const usePrice = ctx?.usePrice ?? useNoPrice;
  const format = usePrice();
  return (item) =>
    format && item.mode === "bookable" && typeof item.priceMad === "number" && item.priceMad > 0
      ? format(item.priceMad)
      : item.priceLabel;
}

function useNoTripBooking(): null {
  return null;
}

/** The host's trip booking adapter, or null when it lends none. */
export function useTripBookingAdapter(): TripBookingAdapter | null {
  const ctx = useContext(DiscoverConfigContext);
  const use = ctx?.useTripBooking ?? useNoTripBooking;
  return use();
}

/**
 * `(mad) => the amount to show` in the guest's currency, or null when the host
 * lends no formatter (the caller then shows dirhams itself).
 */
export function useMadFormatter(): ((madValue: number) => string) | null {
  const ctx = useContext(DiscoverConfigContext);
  const usePrice = ctx?.usePrice ?? useNoPrice;
  return usePrice();
}

export function useDiscoverConfig(): DiscoverConfig {
  const ctx = useContext(DiscoverConfigContext);
  if (!ctx) throw new Error("useDiscoverConfig must be used within <DiscoverApp>");
  return ctx;
}

function useNoSaved(): null {
  return null;
}

/** The host's saved list, or null when it lends none. */
export function useDiscoverSavedAdapter(): DiscoverSavedAdapter | null {
  const ctx = useContext(DiscoverConfigContext);
  const useSaved = ctx?.useSaved ?? useNoSaved;
  return useSaved();
}
